import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { UserTypeService } from '../../services/user-type.service';
import { UserSearchService } from '../../services/user-search.service';
import { PermissionsService } from '../../services/permissions.service';
import { UserType, User, FilePermission } from '../../models/permissions.model';

@Component({
  selector: 'app-permissions-tab',
  templateUrl: './permissions-tab.component.html',
  styleUrls: ['./permissions-tab.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule]
})
export class PermissionsTabComponent implements OnInit, OnDestroy {
  @Input() initialPermissions: FilePermission[] = [];
  @Input() fileId: number | null = null;
  @Output() permissionsChanged = new EventEmitter<FilePermission[]>();

  // User Types (Roles)
  allRoles: UserType[] = [];
  selectedRole: UserType | null = null;
  isLoadingRoles: boolean = false;

  // User Search
  userSearchTerm: string = '';
  filteredUsers: User[] = [];
  isLoadingUsers: boolean = false;
  showUserDropdown: boolean = false;

  // Permissions Data
  filePermissions: FilePermission[] = [];
  isSavingPermissions: boolean = false;
  saveMessage: string = '';
  saveMessageType: 'success' | 'error' = 'success';

  // Pagination
  usersPaginationPage: number = 1;
  usersPaginationPageSize: number = 50;
  
  // For tracking users being loaded by role
  private usersLoadedForRole: User[] = [];

  // Subject for debouncing
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private userTypeService: UserTypeService,
    private userSearchService: UserSearchService,
    private permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    // If fileId is provided, load permissions from API
    if (this.fileId) {
      this.loadFilePermissions();
    }

    // Load user types (roles)
    this.loadUserTypes();

    // Setup debounced search
    this.setupSearchDebounce();
  }

  /**
   * Load all user types/roles
   */
  loadUserTypes(): void {
    this.isLoadingRoles = true;
    this.userTypeService.getUserTypes(1, 100)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.items) {
            this.allRoles = response.data.items;
          }
          this.isLoadingRoles = false;
        },
        error: (err) => {
          console.error('Failed to load user types:', err);
          this.isLoadingRoles = false;
        }
      });
  }

  /**
   * Load file permissions from API with pagination (recursive)
   */
  private loadFilePermissions(): void {
    if (!this.fileId) return;

    this.isLoadingUsers = true;
    this.filePermissions = []; // Clear existing permissions before loading
    this.loadFilePermissionsRecursive(this.fileId, 1);
  }

  /**
   * Recursively load all file permissions (handles pagination)
   */
  private loadFilePermissionsRecursive(fileId: number, pageNumber: number): void {
    this.permissionsService.getFilePermissions(fileId, pageNumber, this.usersPaginationPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data && response.data.length > 0) {
            // Convert API response to FilePermission objects
            const permissions = response.data.map((p) => ({
              id: p.permissionId,
              userId: p.userId,
              iamUserId: p.iamUserId,
              userName: p.userName,
              userEmail: '',
              userOrganization: '',
              userPosition: '',
              userTypeId: 0
            }));

            this.filePermissions.push(...permissions);

            // Check if there are more pages (simple check: if response has data, assume there might be more)
            if (response.data.length === this.usersPaginationPageSize) {
              // Fetch next page recursively
              this.loadFilePermissionsRecursive(fileId, pageNumber + 1);
            } else {
              // All pages loaded
              this.isLoadingUsers = false;
            }
          } else {
            // No data returned, all pages loaded
            this.isLoadingUsers = false;
          }
        },
        error: (err) => {
          console.error('Failed to load file permissions:', err);
          this.isLoadingUsers = false;
        }
      });
  }

  /**
   * Setup debounced user search
   */
  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm: string) => {
        this.performUserSearch(searchTerm);
      });
  }

  /**
   * Handle user search input - uses debounce
   */
  onUserSearchInput(term: string): void {
    this.userSearchTerm = term;
    this.showUserDropdown = term.length > 0;
    this.searchSubject.next(term);
  }

  /**
   * Perform actual user search
   */
  private performUserSearch(searchTerm: string): void {
    if (!searchTerm || searchTerm.trim().length === 0) {
      this.filteredUsers = [];
      this.showUserDropdown = false;
      return;
    }

    this.isLoadingUsers = true;
    this.userSearchService.searchUsersByName(searchTerm, 1, 10)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.items) {
            // Filter out users already in permissions
            const existingUserIds = this.filePermissions.map(p => p.userId);
            this.filteredUsers = response.data.items.filter(user => !existingUserIds.includes(user.id));
          }
          this.isLoadingUsers = false;
        },
        error: (err) => {
          console.error('Failed to search users:', err);
          this.isLoadingUsers = false;
        }
      });
  }

  /**
   * Handle role selection and fetch users for that role
   */
  onRoleSelected(role: UserType): void {
    this.selectedRole = role;
    // Reset pagination and users array for new role
    this.usersLoadedForRole = [];
    this.usersPaginationPage = 1;
    this.loadUsersByRoleRecursive(role.id);
  }

  /**
   * Load users by selected role with pagination
   * Fetches all pages of users for a role and adds them to permissions
   */
  private loadUsersByRoleRecursive(userTypeId: number): void {
    this.isLoadingUsers = true;
    this.userSearchService.getUsersByRole(userTypeId, this.usersPaginationPage, this.usersPaginationPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.items) {
            // Add fetched users to the accumulated array
            this.usersLoadedForRole.push(...response.data.items);
            
            // Check if there are more pages
            const totalCount = response.data.totalCount || 0;
            const currentFetchedCount = this.usersPaginationPage * this.usersPaginationPageSize;
            
            if (currentFetchedCount < totalCount) {
              // Fetch next page recursively
              this.usersPaginationPage++;
              this.loadUsersByRoleRecursive(userTypeId);
            } else {
              // All pages fetched - now add users to permissions grid
              this.addUsersFromRoleToPermissions();
            }
          } else {
            this.isLoadingUsers = false;
          }
        },
        error: (err) => {
          console.error('Failed to load users by role:', err);
          this.isLoadingUsers = false;
        }
      });
  }

  /**
   * Add all loaded users from role to permissions grid
   */
  private addUsersFromRoleToPermissions(): void {
    if (this.usersLoadedForRole.length > 0) {
      // Filter out users already in permissions
      const existingUserIds = this.filePermissions.map(p => p.userId);
      const newUsers = this.usersLoadedForRole.filter(user => !existingUserIds.includes(user.id));
      
      // Add all new users to permissions grid
      newUsers.forEach(user => {
        this.addPermissionForUser(user);
      });
    }
    
    // Clear the dropdown and reset pagination
    this.filteredUsers = [];
    this.showUserDropdown = false;
    this.usersPaginationPage = 1;
    this.usersLoadedForRole = [];
    this.isLoadingUsers = false;
  }

  /**
   * Select user from dropdown
   */
  selectUserFromDropdown(user: User): void {
    this.addPermissionForUser(user);
    this.userSearchTerm = '';
    this.filteredUsers = [];
    this.showUserDropdown = false;
  }

  /**
   * Add user to permissions
   */
  addPermissionForUser(user: User): void {
    // Check if user already exists in permissions using iamUserId (unique identifier)
    const userExists = this.filePermissions.some(p => p.iamUserId === user.iamUserId);
    if (userExists) {
      return;
    }

    const permission: FilePermission = {
      userId: user.id,
      iamUserId: user.iamUserId,
      userName: user.fullName || user.username,
      userEmail: user.email,
      userOrganization: user.organization,
      userPosition: user.position,
      userTypeId: user.userTypeId
    };

    this.filePermissions.push(permission);
    this.permissionsChanged.emit([...this.filePermissions]);

    // Remove from filtered users
    this.filteredUsers = this.filteredUsers.filter(u => u.id !== user.id);
  }

  /**
   * Remove permission by permission object
   * Uses iamUserId as the primary identifier since it's unique per user
   */
  removePermission(permission: FilePermission): void {
    console.log('Removing permission for user:', permission.iamUserId, permission.userName);
    
    const index = this.filePermissions.findIndex(p => 
      p.iamUserId === permission.iamUserId
    );
    
    console.log('Found index:', index, 'Current permissions count:', this.filePermissions.length);
    
    if (index > -1) {
      this.filePermissions.splice(index, 1);
      console.log('Permission removed. New count:', this.filePermissions.length);
      this.permissionsChanged.emit([...this.filePermissions]);
    } else {
      console.warn('Permission not found in array');
    }
  }

  /**
   * Get current permissions
   */
  getPermissions(): FilePermission[] {
    return [...this.filePermissions];
  }

  /**
   * Sync permissions with API
   * Sends the complete list of users who should have access.
   * API determines which permissions to grant, keep, or revoke.
   * An empty list will revoke all permissions.
   * @param fileId - The file ID to sync permissions for
   * @returns Observable that completes when operation done
   */
  assignPermissionsToApi(fileId: number): any {
    if (!fileId) {
      console.warn('No file ID provided');
      return null;
    }

    const iamUserIds = this.filePermissions.map(p => p.iamUserId);
    return this.permissionsService.bulkManagePermissions(fileId, iamUserIds)
      .pipe(takeUntil(this.destroy$));
  }

  /**
   * Save permissions independently (without parent file info)
   * Used when permissions-tab is self-contained
   * Can sync with empty list to remove all permissions
   */
  savePermissions(): void {
    if (!this.fileId) {
      console.warn('No file ID available for saving permissions');
      return;
    }

    this.isSavingPermissions = true;
    const permissionObservable = this.assignPermissionsToApi(this.fileId);

    if (permissionObservable) {
      permissionObservable.subscribe({
        next: (response: any) => {
          if (response && response.success) {
            // Merge API response with local permissions to update IDs
            this.mergeApiResponseWithPermissions(response);
            this.isSavingPermissions = false;
            this.showSuccessMessage('Permissions updated successfully');
          } else {
            console.error('Failed to save permissions:', response?.message);
            this.isSavingPermissions = false;
            this.showErrorMessage('Failed to update permissions');
          }
        },
        error: (error: any) => {
          console.error('Failed to save permissions:', error);
          this.isSavingPermissions = false;
          this.showErrorMessage('Failed to update permissions');
        }
      });
    } else {
      // No file ID available
      this.isSavingPermissions = false;
      this.showErrorMessage('No file ID available');
    }
  }

  /**
   * Show success message and auto-dismiss after 4 seconds
   */
  private showSuccessMessage(message: string): void {
    this.saveMessage = message;
    this.saveMessageType = 'success';
    setTimeout(() => {
      this.saveMessage = '';
    }, 4000);
  }

  /**
   * Merge API response with local filePermissions to update IDs from server
   * Handles the new bulk-manage response format
   */
  private mergeApiResponseWithPermissions(response: any): void {
    if (!response || !response.data) {
      return;
    }

    const { grantedUserIds = [] } = response.data;
    
    // Note: The API returns iamUserIds that were granted/kept
    // We already have iamUserIds in our local permissions
    // The API typically returns permissionId with each grant, but if not,
    // we may need to reload permissions or handle IDs differently
  }

  /**
   * Show error message and auto-dismiss after 4 seconds
   */
  private showErrorMessage(message: string): void {
    this.saveMessage = message;
    this.saveMessageType = 'error';
    setTimeout(() => {
      this.saveMessage = '';
    }, 4000);
  }

  /**
   * Track by function for ngFor
   */
  trackByPermissionId(index: number, permission: FilePermission): number | undefined {
    return permission.id;
  }

  /**
   * Track by function for users
   */
  trackByUserId(index: number, user: User): number {
    return user.id;
  }

  /**
   * Track by function for roles
   */
  trackByRoleId(index: number, role: UserType): number {
    return role.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
